import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';

const API = config.API_URL;
const MAX_FILE_BYTES = 4 * 1024 * 1024; // 4 MB
const ENTITY_TYPES = ['event', 'artist', 'venue', 'festival', 'promoter'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    });
}

function entityLabel(type) {
    if (!type) return '—';
    return type.charAt(0).toUpperCase() + type.slice(1);
}

// ── Upload Modal ──────────────────────────────────────────────────────────────
function UploadModal({ onClose, onUploaded }) {
    const [mode, setMode] = useState('file'); // 'file' | 'camera'
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [caption, setCaption] = useState('');
    const [entityType, setEntityType] = useState('');
    const [entityId, setEntityId] = useState('');
    const [isPublic, setIsPublic] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);

    // ── Camera ────────────────────────────────────────────────────────────────
    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch (e) {
            setError('Camera access denied or unavailable.');
            setMode('file');
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (mode === 'camera') startCamera();
        else stopCamera();
        return stopCamera;
    }, [mode, startCamera, stopCamera]);

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        canvas.toBlob(blob => {
            if (!blob) return;
            const f = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setFile(f);
            setPreview(URL.createObjectURL(f));
            stopCamera();
            setMode('preview');
        }, 'image/jpeg', 0.92);
    };

    const handleFileSelect = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_FILE_BYTES) { setError('File exceeds 4 MB limit.'); return; }
        if (!f.type.startsWith('image/')) { setError('Only image files are allowed.'); return; }
        setError('');
        setFile(f);
        setPreview(URL.createObjectURL(f));
        setMode('preview');
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) handleFileSelect({ target: { files: [f] } });
    };

    // ── Upload flow ───────────────────────────────────────────────────────────
    const toBase64 = (f) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(f);
    });

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError('');
        try {
            const token = await getIdToken();

            // 1. Encode file to base64
            const data = await toBase64(file);

            // 2. Upload to S3 via Lambda
            const uploadRes = await axios.post(
                `${API}/images/upload`,
                { filename: file.name, contentType: file.type, data },
                { headers: { Authorization: token } }
            );
            const { s3Key } = uploadRes.data;

            // 3. Save metadata
            const createRes = await axios.post(
                `${API}/images`,
                {
                    s3Key,
                    caption: caption.trim() || null,
                    entityType: entityType || null,
                    entityId: entityId ? parseInt(entityId) : null,
                    isPublic,
                },
                { headers: { Authorization: token } }
            );

            onUploaded(createRes.data.image);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={S.modal}>
                <div style={S.modalHeader}>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Upload Photo</span>
                    <button onClick={onClose} style={S.closeBtn}>✕</button>
                </div>

                {/* Mode selector */}
                {mode !== 'preview' && (
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                        <button
                            onClick={() => setMode('file')}
                            style={{ ...S.modeBtn, ...(mode === 'file' ? S.modeBtnActive : {}) }}
                        >📁 From Device</button>
                        <button
                            onClick={() => setMode('camera')}
                            style={{ ...S.modeBtn, ...(mode === 'camera' ? S.modeBtnActive : {}) }}
                        >📷 Camera</button>
                    </div>
                )}

                {/* File drop zone */}
                {mode === 'file' && (
                    <div
                        style={S.dropZone}
                        onDrop={handleDrop}
                        onDragOver={e => e.preventDefault()}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖼️</div>
                        <div style={{ color: '#aaa', fontSize: '14px' }}>
                            Drag & drop or click to select
                        </div>
                        <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                            JPEG, PNG, GIF, WEBP · Max 4 MB
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                    </div>
                )}

                {/* Camera viewfinder */}
                {mode === 'camera' && (
                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{ width: '100%', maxHeight: '300px', borderRadius: '8px', objectFit: 'cover' }}
                        />
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                        <button onClick={capturePhoto} style={{ ...S.primaryBtn, marginTop: '10px' }}>
                            📸 Capture Photo
                        </button>
                    </div>
                )}

                {/* Preview */}
                {mode === 'preview' && preview && (
                    <div style={{ marginBottom: '16px' }}>
                        <img
                            src={preview}
                            alt="Preview"
                            style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', borderRadius: '8px', background: '#111' }}
                        />
                        <button
                            onClick={() => { setFile(null); setPreview(null); setMode('file'); }}
                            style={{ ...S.ghostBtn, marginTop: '8px', fontSize: '13px' }}
                        >
                            ← Choose different photo
                        </button>
                    </div>
                )}

                {/* Metadata fields (shown after file is chosen) */}
                {file && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                            <label style={S.label}>Caption (optional)</label>
                            <input
                                type="text"
                                value={caption}
                                onChange={e => setCaption(e.target.value)}
                                placeholder="Describe this photo..."
                                style={S.input}
                                maxLength={280}
                            />
                        </div>

                        <div>
                            <label style={S.label}>Assign to (optional)</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select
                                    value={entityType}
                                    onChange={e => { setEntityType(e.target.value); setEntityId(''); }}
                                    style={{ ...S.input, flex: '0 0 140px' }}
                                >
                                    <option value="">— None —</option>
                                    {ENTITY_TYPES.map(t => (
                                        <option key={t} value={t}>{entityLabel(t)}</option>
                                    ))}
                                </select>
                                {entityType && (
                                    <input
                                        type="number"
                                        value={entityId}
                                        onChange={e => setEntityId(e.target.value)}
                                        placeholder={`${entityLabel(entityType)} ID`}
                                        style={{ ...S.input, flex: 1 }}
                                        min="1"
                                    />
                                )}
                            </div>
                            {entityType && (
                                <div style={{ fontSize: '11px', color: '#777', marginTop: '4px' }}>
                                    Enter the numeric ID of the {entityType}. You can find this in the URL of the {entityType} page.
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                            <button
                                onClick={() => setIsPublic(v => !v)}
                                style={{
                                    ...S.toggleBtn,
                                    backgroundColor: isPublic ? '#2d6a4f' : '#444',
                                    borderColor: isPublic ? '#40916c' : '#555',
                                }}
                            >
                                {isPublic ? '🌐 Public' : '🔒 Private'}
                            </button>
                            <span style={{ fontSize: '12px', color: '#888' }}>
                                {isPublic ? 'Visible on the entity page' : 'Only visible to you'}
                            </span>
                        </div>

                        {error && <div style={S.errorMsg}>{error}</div>}

                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            style={{ ...S.primaryBtn, opacity: uploading ? 0.6 : 1 }}
                        >
                            {uploading ? 'Uploading…' : '⬆️  Upload Photo'}
                        </button>
                    </div>
                )}

                {!file && error && <div style={{ ...S.errorMsg, marginTop: '8px' }}>{error}</div>}
            </div>
        </div>
    );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ image, onClose, onSaved, onDeleted }) {
    const [caption, setCaption] = useState(image.caption || '');
    const [entityType, setEntityType] = useState(image.entity_type || '');
    const [entityId, setEntityId] = useState(image.entity_id ? String(image.entity_id) : '');
    const [isPublic, setIsPublic] = useState(image.is_public !== false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const token = await getIdToken();
            const res = await axios.patch(
                `${API}/images/${image.id}`,
                {
                    caption: caption.trim() || null,
                    entityType: entityType || null,
                    entityId: entityId ? parseInt(entityId) : null,
                    isPublic,
                },
                { headers: { Authorization: token } }
            );
            onSaved(res.data.image);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        setError('');
        try {
            const token = await getIdToken();
            await axios.delete(`${API}/images/${image.id}`, { headers: { Authorization: token } });
            onDeleted(image.id);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Delete failed.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={S.modal}>
                <div style={S.modalHeader}>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Edit Photo</span>
                    <button onClick={onClose} style={S.closeBtn}>✕</button>
                </div>

                <img
                    src={image.image_url}
                    alt={image.caption || ''}
                    style={{ width: '100%', maxHeight: '220px', objectFit: 'contain', borderRadius: '8px', background: '#111', marginBottom: '16px' }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                        <label style={S.label}>Caption</label>
                        <input
                            type="text"
                            value={caption}
                            onChange={e => setCaption(e.target.value)}
                            placeholder="Describe this photo..."
                            style={S.input}
                            maxLength={280}
                        />
                    </div>

                    <div>
                        <label style={S.label}>Assigned To</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <select
                                value={entityType}
                                onChange={e => { setEntityType(e.target.value); setEntityId(''); }}
                                style={{ ...S.input, flex: '0 0 140px' }}
                            >
                                <option value="">— None —</option>
                                {ENTITY_TYPES.map(t => (
                                    <option key={t} value={t}>{entityLabel(t)}</option>
                                ))}
                            </select>
                            {entityType && (
                                <input
                                    type="number"
                                    value={entityId}
                                    onChange={e => setEntityId(e.target.value)}
                                    placeholder={`${entityLabel(entityType)} ID`}
                                    style={{ ...S.input, flex: 1 }}
                                    min="1"
                                />
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                        <button
                            onClick={() => setIsPublic(v => !v)}
                            style={{
                                ...S.toggleBtn,
                                backgroundColor: isPublic ? '#2d6a4f' : '#444',
                                borderColor: isPublic ? '#40916c' : '#555',
                            }}
                        >
                            {isPublic ? '🌐 Public' : '🔒 Private'}
                        </button>
                    </div>

                    {error && <div style={S.errorMsg}>{error}</div>}

                    <button onClick={handleSave} disabled={saving} style={{ ...S.primaryBtn, opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'Saving…' : '💾 Save Changes'}
                    </button>

                    <div style={{ borderTop: '1px solid #333', paddingTop: '12px' }}>
                        {!confirmDelete ? (
                            <button onClick={() => setConfirmDelete(true)} style={S.deleteBtn}>
                                🗑️ Delete Photo
                            </button>
                        ) : (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', color: '#e57373' }}>Delete permanently?</span>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    style={{ ...S.deleteBtn, opacity: deleting ? 0.6 : 1 }}
                                >
                                    {deleting ? 'Deleting…' : 'Yes, Delete'}
                                </button>
                                <button onClick={() => setConfirmDelete(false)} style={S.ghostBtn}>Cancel</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MyImages() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showUpload, setShowUpload] = useState(false);
    const [editImage, setEditImage] = useState(null);
    const [filterType, setFilterType] = useState('all'); // 'all' | entity type | 'unassigned'
    const [lightboxImg, setLightboxImg] = useState(null);

    const loadImages = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = await getIdToken();
            const res = await axios.get(`${API}/images/mine`, { headers: { Authorization: token } });
            setImages(res.data.images || []);
        } catch (e) {
            setError('Failed to load images.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadImages(); }, [loadImages]);

    const handleUploaded = (img) => setImages(prev => [img, ...prev]);
    const handleSaved = (img) => setImages(prev => prev.map(i => i.id === img.id ? img : i));
    const handleDeleted = (id) => setImages(prev => prev.filter(i => i.id !== id));

    // ── Filter ────────────────────────────────────────────────────────────────
    const filtered = images.filter(img => {
        if (filterType === 'all') return true;
        if (filterType === 'unassigned') return !img.entity_type;
        return img.entity_type === filterType;
    });

    const counts = {
        all: images.length,
        unassigned: images.filter(i => !i.entity_type).length,
        ...Object.fromEntries(
            ENTITY_TYPES.map(t => [t, images.filter(i => i.entity_type === t).length])
        ),
    };

    return (
        <div style={S.page}>
            {/* Header */}
            <div style={S.pageHeader}>
                <h1 style={S.pageTitle}>My Images</h1>
                <button onClick={() => setShowUpload(true)} style={S.primaryBtn}>
                    + Upload Photo
                </button>
            </div>

            {/* Filter bar */}
            <div style={S.filterBar}>
                {[
                    { key: 'all', label: 'All' },
                    { key: 'unassigned', label: 'Unassigned' },
                    ...ENTITY_TYPES.map(t => ({ key: t, label: entityLabel(t) + 's' })),
                ].map(({ key, label }) => (
                    counts[key] > 0 || key === 'all' ? (
                        <button
                            key={key}
                            onClick={() => setFilterType(key)}
                            style={{
                                ...S.filterChip,
                                ...(filterType === key ? S.filterChipActive : {}),
                            }}
                        >
                            {label}
                            <span style={S.chipCount}>{counts[key] || 0}</span>
                        </button>
                    ) : null
                ))}
            </div>

            {/* Content */}
            {loading && <div style={S.empty}>Loading…</div>}
            {!loading && error && <div style={S.errorMsg}>{error}</div>}
            {!loading && !error && filtered.length === 0 && (
                <div style={S.empty}>
                    {filterType === 'all'
                        ? 'No photos yet. Click "Upload Photo" to get started!'
                        : `No ${filterType === 'unassigned' ? 'unassigned' : filterType} photos.`}
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <div style={S.grid}>
                    {filtered.map(img => (
                        <div key={img.id} style={S.card}>
                            <div
                                style={S.thumb}
                                onClick={() => setLightboxImg(img)}
                                title="View full size"
                            >
                                <img
                                    src={img.image_url}
                                    alt={img.caption || ''}
                                    style={S.thumbImg}
                                    loading="lazy"
                                />
                                {!img.is_public && (
                                    <span style={S.privateBadge}>🔒</span>
                                )}
                            </div>
                            <div style={S.cardBody}>
                                {img.caption && (
                                    <div style={S.caption} title={img.caption}>{img.caption}</div>
                                )}
                                <div style={S.cardMeta}>
                                    {img.entity_type ? (
                                        <span style={S.entityBadge}>
                                            {entityLabel(img.entity_type)} #{img.entity_id}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#555', fontSize: '12px' }}>Unassigned</span>
                                    )}
                                    <span style={{ color: '#555', fontSize: '11px' }}>{formatDate(img.created_at)}</span>
                                </div>
                                <button
                                    onClick={() => setEditImage(img)}
                                    style={S.editBtn}
                                >
                                    Edit
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Lightbox */}
            {lightboxImg && (
                <div
                    style={{ ...S.overlay, zIndex: 3000 }}
                    onClick={() => setLightboxImg(null)}
                >
                    <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
                        <img
                            src={lightboxImg.image_url}
                            alt={lightboxImg.caption || ''}
                            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }}
                        />
                        {lightboxImg.caption && (
                            <div style={S.lightboxCaption}>{lightboxImg.caption}</div>
                        )}
                        <button
                            onClick={() => setLightboxImg(null)}
                            style={{ ...S.closeBtn, position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}
                        >✕</button>
                    </div>
                </div>
            )}

            {/* Modals */}
            {showUpload && (
                <UploadModal onClose={() => setShowUpload(false)} onUploaded={handleUploaded} />
            )}
            {editImage && (
                <EditModal
                    image={editImage}
                    onClose={() => setEditImage(null)}
                    onSaved={handleSaved}
                    onDeleted={handleDeleted}
                />
            )}
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: {
        padding: '24px',
        maxWidth: '1100px',
        margin: '0 auto',
        color: '#e0e0e0',
        fontFamily: 'inherit',
    },
    pageHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    pageTitle: {
        fontSize: '24px',
        fontWeight: 700,
        margin: 0,
        color: '#e0e0e0',
    },
    filterBar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '20px',
    },
    filterChip: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '5px 12px',
        borderRadius: '20px',
        border: '1px solid #444',
        background: '#2c2c2c',
        color: '#aaa',
        fontSize: '13px',
        cursor: 'pointer',
    },
    filterChipActive: {
        borderColor: '#bb86fc',
        background: '#2a2040',
        color: '#bb86fc',
    },
    chipCount: {
        background: '#333',
        borderRadius: '10px',
        padding: '1px 6px',
        fontSize: '11px',
        color: '#888',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '16px',
    },
    card: {
        background: '#1e1e1e',
        border: '1px solid #333',
        borderRadius: '10px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    thumb: {
        position: 'relative',
        width: '100%',
        paddingTop: '75%', // 4:3
        overflow: 'hidden',
        cursor: 'zoom-in',
        background: '#111',
    },
    thumbImg: {
        position: 'absolute',
        top: 0, left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    privateBadge: {
        position: 'absolute',
        top: '6px',
        right: '6px',
        fontSize: '14px',
        background: 'rgba(0,0,0,0.6)',
        borderRadius: '4px',
        padding: '2px 4px',
    },
    cardBody: {
        padding: '10px 12px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        flex: 1,
    },
    caption: {
        fontSize: '13px',
        color: '#ccc',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    cardMeta: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'wrap',
    },
    entityBadge: {
        fontSize: '11px',
        background: '#2a2040',
        color: '#bb86fc',
        border: '1px solid #3d3060',
        borderRadius: '4px',
        padding: '2px 6px',
    },
    editBtn: {
        marginTop: '4px',
        padding: '5px 10px',
        background: 'transparent',
        border: '1px solid #444',
        borderRadius: '5px',
        color: '#aaa',
        fontSize: '12px',
        cursor: 'pointer',
        alignSelf: 'flex-end',
    },
    empty: {
        textAlign: 'center',
        color: '#666',
        marginTop: '60px',
        fontSize: '15px',
    },
    // Modal
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '16px',
    },
    modal: {
        background: '#1e1e1e',
        border: '1px solid #333',
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '480px',
        maxHeight: '90vh',
        overflowY: 'auto',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    closeBtn: {
        background: 'transparent',
        border: 'none',
        color: '#888',
        fontSize: '18px',
        cursor: 'pointer',
        lineHeight: 1,
    },
    dropZone: {
        border: '2px dashed #444',
        borderRadius: '10px',
        padding: '36px 20px',
        textAlign: 'center',
        cursor: 'pointer',
        marginBottom: '16px',
        transition: 'border-color 0.2s',
    },
    label: {
        display: 'block',
        fontSize: '12px',
        color: '#888',
        marginBottom: '5px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    input: {
        width: '100%',
        background: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        color: '#e0e0e0',
        padding: '8px 10px',
        fontSize: '14px',
        boxSizing: 'border-box',
    },
    primaryBtn: {
        background: '#bb86fc',
        color: '#1a1a1a',
        border: 'none',
        borderRadius: '6px',
        padding: '9px 18px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    ghostBtn: {
        background: 'transparent',
        border: '1px solid #555',
        borderRadius: '6px',
        color: '#aaa',
        padding: '7px 14px',
        fontSize: '13px',
        cursor: 'pointer',
    },
    deleteBtn: {
        background: 'transparent',
        border: '1px solid #e57373',
        borderRadius: '6px',
        color: '#e57373',
        padding: '7px 14px',
        fontSize: '13px',
        cursor: 'pointer',
    },
    toggleBtn: {
        border: '1px solid',
        borderRadius: '20px',
        padding: '5px 14px',
        fontSize: '13px',
        cursor: 'pointer',
        color: '#e0e0e0',
    },
    modeBtn: {
        flex: 1,
        background: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        color: '#aaa',
        padding: '8px',
        fontSize: '13px',
        cursor: 'pointer',
    },
    modeBtnActive: {
        borderColor: '#bb86fc',
        color: '#bb86fc',
        background: '#2a2040',
    },
    errorMsg: {
        color: '#e57373',
        background: '#2a1a1a',
        border: '1px solid #6b2c2c',
        borderRadius: '6px',
        padding: '8px 12px',
        fontSize: '13px',
    },
    lightboxCaption: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(0,0,0,0.7)',
        color: '#e0e0e0',
        padding: '10px 14px',
        fontSize: '14px',
        borderRadius: '0 0 8px 8px',
        textAlign: 'center',
    },
};
